/**
 * Parse SDK Initialization Module
 * 
 * OPTIMIZATION: Single point of Parse initialization to avoid redundant calls.
 * Import this module at app startup or before any Parse operations.
 * 
 * This module ensures Parse is initialized exactly once, regardless of
 * how many times it's imported across the codebase.
 */

import Parse from 'parse'
import { BACK4APP_CONFIG } from '../config/back4app.config'

let isInitialized = false

/**
 * Initialize Parse SDK if not already done
 * Safe to call multiple times - only initializes once
 */
export function initializeParse(): void {
  if (isInitialized || Parse.applicationId) {
    return
  }

  try {
    Parse.initialize(BACK4APP_CONFIG.applicationId, BACK4APP_CONFIG.javascriptKey)
    Parse.serverURL = BACK4APP_CONFIG.serverURL

    // Initialize LiveQuery if available
    if (BACK4APP_CONFIG.liveQueryServerURL) {
      try {
        Parse.liveQueryServerURL = BACK4APP_CONFIG.liveQueryServerURL
      } catch (error) {
        console.warn('[Parse] LiveQuery initialization failed:', error)
      }
    }

    isInitialized = true
    console.log('[Parse] SDK initialized successfully')
  } catch (error) {
    console.error('[Parse] Failed to initialize SDK:', error)
    throw error
  }
}

/**
 * Ensure Parse is initialized before using it
 * Returns the Parse module for convenience
 */
export function ensureParseInitialized(): typeof Parse {
  initializeParse()
  return Parse
}

/**
 * Check if Parse is initialized
 */
export function isParseInitialized(): boolean {
  return isInitialized || !!Parse.applicationId
}

// Auto-initialize when this module is imported
initializeParse()

export default Parse

